import EmptyState from '@Guidelines/Component/EmptyState';
import useCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useCurrentFacility';
import { Building2 } from 'lucide-react';
import type * as React from 'react';
import { Spinner } from '@verclam/ui-kit';

type Props = {
	readonly children: React.ReactNode;
};

/**
 * Creates the invariant `useStrictCurrentFacility` relies on: a screen mounted under this guard never
 * renders with a `null` current facility. Unlike `RequireSession`, it never redirects — `/facilities`
 * is where a facility gets created or picked, but it is not reachable by every caller (it requires its
 * own `FACILITY_READ` / `STAFF_READ` permission), and bouncing a caller without it there would loop
 * back and forth forever. It renders an explicit state in place of the guarded screen instead.
 */
const RequireFacility: React.FC<Props> = ({ children }) => {
	const { currentFacility, facilities, isResolved } = useCurrentFacility();

	if (!isResolved) {
		return <Spinner aria-label="Résolution de l'agence courante" />;
	}

	if (facilities.length === 0) {
		return (
			<EmptyState
				title="Aucune agence"
				description="Créez votre première agence depuis Agences pour commencer."
				icon={<Building2 size={28} />}
			/>
		);
	}

	if (currentFacility === null) {
		return (
			<EmptyState
				title="Aucune agence sélectionnée"
				description="Choisissez une agence dans le sélecteur en haut de page pour accéder à son contenu."
				icon={<Building2 size={28} />}
			/>
		);
	}

	return children;
};

export default RequireFacility;
